import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  can,
  formatPoNumber,
  formatQty,
  qtyToHundredths,
  type LinkBackorderSupplyInput,
} from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { createIntentKey } from '../../lib/idempotency';
import { useSession } from '../session';
import { useOnline } from '../ui/useConnection';
import { SectionCard } from '../ui/Detail';
import { Notice } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Field, field } from '../create/Field';
import {
  StaleNotice,
  UnresolvedState,
  refreshFailed,
  shownState,
  worstState,
  worstError,
} from '../ui/QueryStates';
import { IconInventory } from '../icons';
import {
  CommercialWriteRecovery,
  useCommercialWriteRecovery,
} from './CommercialWriteRecovery';

export function WorkOrderBackordersCard({ workOrderId }: { workOrderId: string }) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const session = useSession();
  const online = useOnline();
  const allowed = can(session.role, 'inventory', 'manage');
  const q = useQuery({
    ...trpc.inventoryReservations.workOrder.queryOptions({ workOrderId }),
    enabled: allowed,
  });
  const scope = useQuery({
    ...trpc.workOrder.get.queryOptions({ workOrderId }),
    enabled: allowed,
  });
  const [keys] = useState(() => createIntentKey());
  const reserve = useMutation(
    trpc.inventoryReservations.reserve.mutationOptions({
      onSuccess: () => {
        keys.clear();
        void qc.invalidateQueries();
      },
    }),
  );
  if (!allowed) return null;
  const state = worstState(shownState(q, q.data), shownState(scope, scope.data));
  const busy =
    !online ||
    q.isFetching ||
    scope.isFetching ||
    q.isError ||
    scope.isError ||
    reserve.isPending;
  return (
    <SectionCard title="Material allocations and backorders">
      <div className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Reservations hold available stock for this work order. Backorders show the
          remainder. Recorded use consumes stock; billing does not consume it again.
        </p>
        <StaleNotice
          show={
            refreshFailed(q, q.data) ||
            refreshFailed(scope, scope.data) ||
            q.isPaused ||
            scope.isPaused
          }
          onRetry={() => {
            void q.refetch();
            void scope.refetch();
          }}
        />
        {reserve.error ? (
          <Notice tone="danger" alert>
            {reserve.error.message}
          </Notice>
        ) : null}
        {state !== 'ready' ? (
          <UnresolvedState
            state={state}
            icon={IconInventory}
            what="work order allocations"
            error={worstError(q.error, scope.error)}
            onRetry={() => {
              void q.refetch();
              void scope.refetch();
            }}
          />
        ) : q.data && scope.data ? (
          <>
            <Button
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={() =>
                reserve.mutate({
                  workOrderId,
                  ...keys.forIntent([
                    session.orgId,
                    session.userId,
                    workOrderId,
                    q.dataUpdatedAt,
                  ]),
                })
              }
            >
              {reserve.isPending ? 'Checking stock…' : 'Reserve newly available stock'}
            </Button>
            {q.data.lines.length ? (
              q.data.lines.map((allocation) => {
                const line = scope.data.lines.find(
                  (value) => value.id === allocation.lineId,
                );
                const task = scope.data.tasks.find(
                  (value) => value.id === allocation.taskId,
                );
                return (
                  <section
                    key={allocation.lineId}
                    className="space-y-3 rounded-[var(--radius)] border border-border p-3"
                  >
                    <div>
                      <h3 className="text-sm font-semibold">
                        {line?.description ?? 'Material line'}
                      </h3>
                      <p className="text-xs text-muted-foreground">
                        {task?.name ?? 'Unassigned task'}
                      </p>
                    </div>
                    <dl className="grid grid-cols-2 gap-3 text-xs sm:grid-cols-4">
                      {[
                        ['Required', allocation.quantityHundredths],
                        ['Recorded use', allocation.usedHundredths],
                        ['Reserved', allocation.reservedHundredths],
                        ['Backorder', allocation.backorderHundredths],
                      ].map(([label, value]) => (
                        <div key={label}>
                          <dt className="text-muted-foreground">{label}</dt>
                          <dd className="mt-1 text-base font-semibold tabular-nums">
                            {formatQty(value as number)} {line?.unitLabel}
                          </dd>
                        </div>
                      ))}
                    </dl>
                    <ul className="space-y-2 text-xs">
                      {allocation.supplyLinks.map((supply) => (
                        <li key={supply.id}>
                          <Link
                            className="font-semibold hover:underline"
                            to="/purchase-orders/$purchaseOrderId"
                            params={{ purchaseOrderId: supply.purchaseOrderId }}
                          >
                            {formatPoNumber(supply.poNumber)}
                          </Link>{' '}
                          · {supply.status.replaceAll('_', ' ')} ·{' '}
                          {formatQty(supply.outstandingHundredths)} linked incoming ·{' '}
                          {formatQty(supply.receivedAllocatedHundredths)} received and
                          allocated
                          <ReleaseSupply
                            id={supply.id}
                            disabled={busy || supply.outstandingHundredths <= 0}
                          />
                        </li>
                      ))}
                    </ul>
                    {line ? (
                      <AllocationActions
                        key={`${line.id}:${line.version}`}
                        lineId={line.id}
                        itemId={line.itemId}
                        version={line.version}
                        sourceLocationId={line.sourceLocationId}
                        disabled={busy}
                        availableSupply={allocation.availableSupply}
                      />
                    ) : (
                      <Notice tone="warn">
                        Refresh the work order before changing this allocation.
                      </Notice>
                    )}
                  </section>
                );
              })
            ) : (
              <p className="text-sm text-muted-foreground">
                This work order has no tracked material requirements.
              </p>
            )}
          </>
        ) : null}
      </div>
    </SectionCard>
  );
}

function AllocationActions({
  lineId,
  itemId,
  version,
  sourceLocationId,
  disabled,
  availableSupply,
}: {
  lineId: string;
  itemId: string | null;
  version: number;
  sourceLocationId: string | null;
  disabled: boolean;
  availableSupply: {
    purchaseOrderId: string;
    purchaseOrderLineId: string;
    poNumber: number;
    status: string;
    availableHundredths: number;
    linkableHundredths: number;
    expectedDate: string | null;
  }[];
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const session = useSession();
  const [open, setOpen] = useState(false);
  const [vendorId, setVendorId] = useState('');
  const [destination, setDestination] = useState(sourceLocationId ?? '');
  const [reason, setReason] = useState('');
  const [purchaseOrderId, setPurchaseOrderId] = useState('');
  const [purchaseOrderLineId, setPurchaseOrderLineId] = useState('');
  const [quantity, setQuantity] = useState('');
  const recovery = useCommercialWriteRecovery('inventoryReservations.linkSupply', lineId);
  const online = useOnline();
  const [keys] = useState(() => createIntentKey());
  const locations = useQuery({
    ...trpc.inventory.locations.queryOptions(),
    enabled: open,
  });
  const vendors = useQuery({ ...trpc.purchasing.vendors.queryOptions(), enabled: open });
  const orders = useQuery({ ...trpc.purchasing.list.queryOptions(), enabled: open });
  const order = useQuery({
    ...trpc.purchasing.get.queryOptions({ purchaseOrderId }),
    enabled: open && Boolean(purchaseOrderId),
  });
  const success = () => {
    keys.clear();
    void qc.invalidateQueries();
  };
  const create = useMutation(
    trpc.inventoryReservations.createPurchaseOrder.mutationOptions({
      onSuccess: success,
    }),
  );
  const link = useMutation(
    trpc.inventoryReservations.linkSupply.mutationOptions({
      onSuccess: async (_result, request) => {
        if (await recovery.complete(request)) success();
      },
    }),
  );
  const reallocate = useMutation(
    trpc.inventoryReservations.reallocate.mutationOptions({ onSuccess: success }),
  );
  const busy =
    disabled ||
    create.isPending ||
    link.isPending ||
    reallocate.isPending ||
    recovery.preparing ||
    recovery.read.status !== 'empty';
  const error = create.error ?? link.error ?? reallocate.error;
  const intent = (payload: unknown) =>
    keys.forIntent([session.orgId, session.userId, payload]);
  const sendSupply = async (
    payload?: Omit<LinkBackorderSupplyInput, 'clientId' | 'mutationId'>,
  ) => {
    if (!online || link.isPending || recovery.preparing) return;
    const request =
      recovery.read.status === 'ready'
        ? recovery.read.request
        : payload
          ? { ...payload, ...intent(['link', payload]) }
          : null;
    if (request && (await recovery.prepare(request))) link.mutate(request);
  };
  return (
    <details open={open} onToggle={(event) => setOpen(event.currentTarget.open)}>
      <summary className="min-h-11 cursor-pointer py-3 text-xs font-semibold">
        Manage supply and source
      </summary>
      <div className="space-y-4 border-t border-border pt-3">
        <CommercialWriteRecovery
          read={recovery.read}
          error={recovery.error}
          pending={link.isPending || recovery.preparing}
          online={online}
          onRetry={() => void sendSupply()}
          onResolve={() =>
            void recovery.resolveAttempt().then((resolved) => {
              if (resolved) {
                link.reset();
                keys.clear();
              }
            })
          }
        />
        {error ? (
          <Notice tone="danger" alert>
            {error.message}
          </Notice>
        ) : null}
        {create.isSuccess ? (
          <Notice tone="success">
            Draft purchase order created. Review it before sending to a supplier.
          </Notice>
        ) : null}
        <StaleNotice
          show={
            refreshFailed(locations, locations.data) ||
            refreshFailed(vendors, vendors.data) ||
            refreshFailed(orders, orders.data)
          }
          onRetry={() => {
            void locations.refetch();
            void vendors.refetch();
            void orders.refetch();
          }}
        />
        {availableSupply.length > 0 ? (
          <section className="space-y-2" aria-label="Compatible incoming supply">
            <h4 className="text-xs font-semibold">Existing incoming supply</h4>
            <p className="text-xs text-muted-foreground">
              Link available incoming quantities before creating another purchase order
              for the remaining shortage.
            </p>
            <ul className="space-y-3 text-xs">
              {availableSupply.map((supply) => (
                <li key={supply.purchaseOrderLineId} className="space-y-1">
                  <Link
                    className="font-semibold underline"
                    to="/purchase-orders/$purchaseOrderId"
                    params={{ purchaseOrderId: supply.purchaseOrderId }}
                  >
                    {formatPoNumber(supply.poNumber)}
                  </Link>{' '}
                  · {supply.status.replaceAll('_', ' ')} ·{' '}
                  {formatQty(supply.availableHundredths)} unclaimed incoming ·{' '}
                  {formatQty(supply.linkableHundredths)} can cover this line
                  {supply.expectedDate
                    ? ` · Expected ${supply.expectedDate}`
                    : ' · Receipt date unknown'}
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={busy || supply.linkableHundredths <= 0}
                    onClick={() => {
                      const payload = {
                        workOrderLineId: lineId,
                        purchaseOrderLineId: supply.purchaseOrderLineId,
                        quantityHundredths: supply.linkableHundredths,
                      };
                      void sendSupply(payload);
                    }}
                  >
                    Link existing supply
                  </Button>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
        <form
          className="space-y-2"
          onSubmit={(event) => {
            event.preventDefault();
            const payload = {
              workOrderLineId: lineId,
              vendorId,
              expectedReceiptLocationId: sourceLocationId ?? undefined,
            };
            create.mutate({ ...payload, ...intent(['draft', payload]) });
          }}
        >
          <Field label="Supplier for remaining shortage">
            <select
              className={field}
              value={vendorId}
              disabled={busy || !vendors.data || vendors.isError}
              onChange={(event) => setVendorId(event.target.value)}
            >
              <option value="">Choose supplier</option>
              {(vendors.data ?? [])
                .filter((vendor) => vendor.active)
                .map((vendor) => (
                  <option key={vendor.id} value={vendor.id}>
                    {vendor.name}
                  </option>
                ))}
            </select>
          </Field>
          <Button size="sm" type="submit" disabled={busy || !vendorId || vendors.isError}>
            Create draft purchase order
          </Button>
          {vendors.isError || vendors.isPaused ? (
            <Notice tone="warn">
              Suppliers are unavailable. Refresh before ordering.
            </Notice>
          ) : null}
        </form>
        <form
          className="space-y-2 border-t border-border pt-3"
          onSubmit={(event) => {
            event.preventDefault();
            const payload = {
              workOrderLineId: lineId,
              purchaseOrderLineId,
              quantityHundredths: qtyToHundredths(quantity),
            };
            void sendSupply(payload);
          }}
        >
          <Field label="Link existing incoming supply">
            <select
              className={field}
              value={purchaseOrderId}
              disabled={busy || !orders.data || orders.isError}
              onChange={(event) => {
                setPurchaseOrderId(event.target.value);
                setPurchaseOrderLineId('');
              }}
            >
              <option value="">Choose purchase order</option>
              {(orders.data ?? []).map((entry) => (
                <option key={entry.po.id} value={entry.po.id}>
                  {formatPoNumber(entry.po.poNumber)} · {entry.vendorName} ·{' '}
                  {entry.po.status}
                </option>
              ))}
            </select>
          </Field>
          {purchaseOrderId ? (
            <Field label="Purchase order line">
              <select
                className={field}
                value={purchaseOrderLineId}
                disabled={busy || !order.data || order.isError}
                onChange={(event) => setPurchaseOrderLineId(event.target.value)}
              >
                <option value="">Choose matching item</option>
                {(order.data?.lines ?? [])
                  .filter((line) => line.itemId === itemId)
                  .map((line) => (
                    <option key={line.id} value={line.id}>
                      {line.name} · {formatQty(line.outstandingHundredths)} outstanding
                    </option>
                  ))}
              </select>
            </Field>
          ) : null}
          {order.isError || (purchaseOrderId && order.isPaused) ? (
            <Notice tone="warn">
              Purchase order lines are unavailable.
              <Button variant="ghost" size="sm" onClick={() => void order.refetch()}>
                Retry
              </Button>
            </Notice>
          ) : null}
          <Field label="Quantity to link">
            <input
              className={field}
              type="number"
              step="0.01"
              min="0.01"
              value={quantity}
              disabled={busy}
              onChange={(event) => setQuantity(event.target.value)}
            />
          </Field>
          <Button
            size="sm"
            type="submit"
            disabled={busy || !purchaseOrderLineId || !quantity || order.isError}
          >
            Link supply
          </Button>
        </form>
        <form
          className="space-y-2 border-t border-border pt-3"
          onSubmit={(event) => {
            event.preventDefault();
            const payload = {
              workOrderLineId: lineId,
              sourceLocationId: destination,
              expectedVersion: version,
              reason: reason.trim(),
            };
            reallocate.mutate({ ...payload, ...intent(['reallocate', payload]) });
          }}
        >
          <p className="text-xs text-muted-foreground">
            Changing source releases this line's held stock and unused purchase-order
            coverage, then reserves available stock at the new source. It does not
            physically move stock.
          </p>
          <Field label="New stock source">
            <select
              className={field}
              value={destination}
              disabled={busy || !locations.data || locations.isError}
              onChange={(event) => setDestination(event.target.value)}
            >
              <option value="">Choose location</option>
              {(locations.data ?? []).map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Reason for changing source">
            <input
              className={field}
              value={reason}
              maxLength={500}
              disabled={busy}
              onChange={(event) => setReason(event.target.value)}
            />
          </Field>
          <Button
            size="sm"
            type="submit"
            disabled={busy || !destination || !reason.trim() || locations.isError}
          >
            Change source and reallocate
          </Button>
        </form>
      </div>
    </details>
  );
}

function ReleaseSupply({ id, disabled }: { id: string; disabled: boolean }) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const session = useSession();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [keys] = useState(() => createIntentKey());
  const release = useMutation(
    trpc.inventoryReservations.releaseSupply.mutationOptions({
      onSuccess: () => {
        keys.clear();
        setOpen(false);
        setReason('');
        void qc.invalidateQueries();
      },
    }),
  );
  return (
    <span className="block space-y-2">
      {!open ? (
        <Button
          size="sm"
          variant="ghost"
          disabled={disabled}
          onClick={() => setOpen(true)}
        >
          Release unused supply link
        </Button>
      ) : (
        <>
          <Field label="Reason for releasing supply">
            <input
              className={field}
              value={reason}
              maxLength={500}
              disabled={release.isPending}
              onChange={(event) => setReason(event.target.value)}
            />
          </Field>
          <Button
            size="sm"
            disabled={disabled || release.isPending || !reason.trim()}
            onClick={() => {
              const payload = { supplyLinkId: id, reason: reason.trim() };
              release.mutate({
                ...payload,
                ...keys.forIntent([session.orgId, session.userId, payload]),
              });
            }}
          >
            Release link
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={release.isPending}
            onClick={() => setOpen(false)}
          >
            Cancel
          </Button>
        </>
      )}
      {release.error ? (
        <Notice tone="danger" alert>
          {release.error.message}
        </Notice>
      ) : null}
    </span>
  );
}
